import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import QuestionCard from "@/components/QuestionCard";
import { TYPE_COLORS } from "@/lib/types";
import { KineticText, Reveal } from "@/components/motion/primitives";
import { ChevronLeft, Tag, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { TYPE_FAMILIES } from "@/lib/questionTypes";
import { LoadError, isNotFound } from "@/components/LoadError";

const TYPE_ORDER = TYPE_FAMILIES.flatMap((f) => f.types.map((t) => t.name));

export default function Type() {
  const { type } = useParams();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["type", type],
    queryFn: async () => (await api.get(`/types/${encodeURIComponent(type)}`)).data,
  });
  const { data: all } = useQuery({
    queryKey: ["types"],
    queryFn: async () => (await api.get("/types")).data,
  });

  const others = useMemo(() => {
      return [...(all?.types || [])].sort((a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type)).filter((t) => t.type !== data?.type);
  }, [all, data?.type]);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (isError && !isNotFound(error)) return <LoadError onRetry={refetch} />;
  if (isError || !data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-serif text-3xl font-bold text-foreground">Type not found</h1>
        <p className="mt-3 text-muted-foreground">We don&apos;t have a question type called &ldquo;{type}&rdquo;.</p>
        <Link to="/explore/types" className="mt-6 inline-block text-primary hover:underline">Browse all types</Link>
      </div>
    );
  }

  const typeClass = TYPE_COLORS[data.type] || "bg-muted text-muted-foreground";
  const page = data.page || {};

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8" data-testid="type-page">
      <Seo title={page.title || `${data.type} questions in the Bible`} description={page.seo || `Every ${data.type.toLowerCase()} question in Scripture.`} />
      <Link
        to="/explore/types"
        data-testid="type-back-link"
        className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> All question types
      </Link>

      <header className="mb-10 border-b border-border pb-8">
        <span className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-gold">
          <Tag className="h-4 w-4" /> Question type
        </span>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <KineticText as="h1" text={data.type} className="block font-serif text-5xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-6xl" testId="type-title" />
          {data.count > 0 && <span className={`rounded-full px-3 py-1 text-sm font-semibold ${typeClass}`}>{data.count} question{data.count === 1 ? "" : "s"}</span>}
        </div>
        {page.subtitle && <p className="mt-3 font-serif text-xl italic text-gold sm:text-2xl" data-testid="type-subtitle">{page.subtitle}</p>}
        {page.intro && (
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg" data-testid="type-intro">
            {page.intro}
          </p>
        )}
      </header>

      {page.invitation && (
        <Reveal>
          <aside className="mb-10 rounded-2xl border-l-4 border-gold bg-accent/10 p-6 sm:p-8" data-testid="type-invitation">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">The invitation</span>
            <p className="mt-3 max-w-3xl font-serif text-lg leading-relaxed text-foreground sm:text-xl">{page.invitation}</p>
          </aside>
        </Reveal>
      )}

      {data.questions.length === 0 && (
        <p className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground" data-testid="type-empty">No questions are tagged with this kind yet. Check back soon.</p>
      )}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="type-questions">
        {data.questions.map((q, i) => (
          <Reveal key={q.id} delay={(i % 2) * 0.08}>
            <QuestionCard q={q} index={i} />
          </Reveal>
        ))}
      </div>

      {all?.types?.length > 1 && (
        <div className="mt-16 border-t border-border pt-8" data-testid="other-types">
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">Other kinds of questions</p>
          <div className="flex flex-wrap gap-2">
            {others.map((t) => (
              <Link
                key={t.type}
                to={`/type/${encodeURIComponent(t.type)}`}
                data-testid={`type-chip-${t.type.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-transform hover:scale-105 ${TYPE_COLORS[t.type] || "bg-muted text-muted-foreground"}`}
              >
                {t.type} <span className="font-mono text-xs opacity-70">{t.count}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
